<template>
    <div class="flex flex-col gap-5">
        <ExecutiveSummarySection v-if="report.executive_summary" :summary="report.executive_summary" />

        <SelfCategoryScoresSection
            v-if="aggregates && hasCategoryScores"
            :sections="filteredSections"
            :max-score="maxScore"
            :intro="t('reflections.campaignReport.sectionsIntro')" />

        <section v-if="availableFilters.length > 0" class="flex flex-col gap-3">
            <BaseRadioGroup
                v-model="relationshipFilter"
                :label="t('reflections.campaignReport.filterLabel')"
                orientation="horizontal"
                :options="
                    availableFilters.map((opt) => ({
                        value: opt.value,
                        label: opt.count !== null ? t('reflections.campaignReport.filterOption', { label: opt.label, count: opt.count }) : opt.label,
                    }))
                " />
            <p v-if="relationshipFilter !== 'all'" class="details text-ink-muted">
                {{ t("reflections.campaignReport.filterNote") }}
            </p>
        </section>

        <section v-for="panel in sectionPanels" :key="panel.section.section_id" class="flex flex-col gap-2.5">
            <p class="body-small-semibold text-ink">{{ t("reflections.campaignReport.sectionInsight") }}</p>
            <CampSectionInsight :headline="panel.section.insight?.headline || undefined" :chart-rows="panel.chartRows" :insight="panel.section.insight ?? null" />
        </section>

        <section v-if="openTextGroups.length > 0" class="flex flex-col gap-3">
            <p class="body-small-semibold text-ink">{{ t("reflections.campaignReport.openTextHeading") }}</p>
            <CampOpenTextSection v-for="group in openTextGroups" :key="group.questionId" :title="group.title" :response-count="group.responseCount" :themes="group.themes" />
        </section>

        <section v-if="detailedRows.length > 0" class="flex flex-col gap-2.5">
            <p class="body-small-semibold text-ink">{{ t("reflections.campaignReport.detailedBreakdown") }}</p>
            <BaseCard padding="lg" class="flex flex-col gap-10">
                <CampSegmentedBar v-for="row in detailedRows" :key="row.key" :label="row.label" :distribution="row.distribution" :avg="row.avg" />
                <CampLegend />
            </BaseCard>
        </section>
    </div>
</template>

<script setup lang="ts">
import type { CampaignReportPayload } from "~vue/api/generated/reflections";
import ExecutiveSummarySection from "~vue/AssessmentResults/ExecutiveSummarySection.vue";
import type { Aggregates, Cut, PerQuestion, SectionAggregate } from "~vue/AssessmentResults/types";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseRadioGroup from "~vue/components/form/BaseRadioGroup.vue";
import CampLegend from "~vue/pages/reflections/CampLegend.vue";
import CampOpenTextSection from "~vue/pages/reflections/CampOpenTextSection.vue";
import CampSectionInsight from "~vue/pages/reflections/CampSectionInsight.vue";
import CampSegmentedBar from "~vue/pages/reflections/CampSegmentedBar.vue";
import SelfCategoryScoresSection from "~vue/pages/reflections/SelfCategoryScoresSection.vue";
import { resolveQuestionName } from "~vue/utils/assessmentValidation";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

// The Nadia synthesis + score sections of an org-wide pooled report, shared by
// the campaign results page and the Individual 360s template report page.
interface Props {
    report: CampaignReportPayload;
}

const props = defineProps<Props>();
const { t } = useI18n();

const relationshipFilter = ref<string>("all");

const aggregates = computed<Aggregates | null>(() => props.report.aggregates ?? null);
const hasCategoryScores = computed(() => (aggregates.value?.sections ?? []).some((s) => s.avg !== null));

const maxScore = computed(() => {
    const first = aggregates.value?.per_question?.find((q) => q.is_score);
    return first?.max_score ?? first?.distribution?.length ?? 5;
});

const availableFilters = computed<{ value: string; label: string; count: number | null }[]>(() => {
    const sections = aggregates.value?.sections ?? [];
    const totals = new Map<string, { label: string; count: number }>();
    for (const s of sections) {
        for (const cut of s.cuts ?? []) {
            const cur = totals.get(cut.relationship);
            if (cur) {
                cur.count += cut.count;
            } else {
                totals.set(cut.relationship, { label: cut.label, count: cut.count });
            }
        }
    }
    const opts: { value: string; label: string; count: number | null }[] = [{ value: "all", label: t("reflectionsAdmin.panel.filter.all"), count: null }];
    for (const [value, { label, count }] of totals) {
        if (count > 0) {
            opts.push({ value, label, count });
        }
    }
    return opts.length > 1 ? opts : [];
});

function applyCutToQuestion(q: PerQuestion): PerQuestion {
    if (relationshipFilter.value === "all") {
        return q;
    }
    const cut: Cut | undefined = q.cuts?.find((c) => c.relationship === relationshipFilter.value);
    if (!cut || !cut.distribution) {
        return { ...q, all_avg: null, all_count: 0, distribution: (q.distribution ?? []).map(() => 0) };
    }
    return { ...q, all_avg: cut.avg, all_count: cut.count, distribution: cut.distribution };
}

function applyCutToSection(s: SectionAggregate): SectionAggregate {
    if (relationshipFilter.value === "all") {
        return s;
    }
    const cut = (s.cuts ?? []).find((c) => c.relationship === relationshipFilter.value);
    if (!cut || !cut.distribution) {
        return { ...s, avg: null, count: 0, distribution: (s.distribution ?? []).map(() => 0) };
    }
    return { ...s, avg: cut.avg, count: cut.count, distribution: cut.distribution };
}

// Campaign questions use the `{{ name }}` placeholder because the templates
// power both 360s and self reflections. A campaign has many subjects, so
// there's no single first name to substitute — use a generic noun.
function labelFor(q: PerQuestion): string {
    return resolveQuestionName(q.text || q.title, t("reflections.campaignReport.genericSubject"));
}

const filteredSections = computed<SectionAggregate[]>(() => (aggregates.value?.sections ?? []).map(applyCutToSection));
const filteredScoreQuestions = computed<PerQuestion[]>(() => (aggregates.value?.per_question ?? []).filter((q) => q.is_score).map(applyCutToQuestion));

interface SectionPanel {
    section: SectionAggregate;
    chartRows: { key: string; label: string; distribution: number[]; avg: number | null }[];
}

// Top-level sections drive the "Section Insight" cards. Each card's chart rows
// are the section's score questions (rolled up with the current rater filter
// applied). If a section has no scored questions, the chart is omitted and only
// the LLM insight renders.
const sectionPanels = computed<SectionPanel[]>(() => {
    const sections = filteredSections.value;
    if (sections.length === 0) {
        return [];
    }
    const topLevel = sections.filter((s) => s.depth === 0).sort((a, b) => a.order - b.order);

    return topLevel.map((section) => {
        const subsectionIds = new Set<string>([section.section_id]);
        for (const s of sections) {
            if (s.parent_section_id === section.section_id) {
                subsectionIds.add(s.section_id);
            }
        }
        const chartRows = filteredScoreQuestions.value
            .filter((q) => q.section_id && subsectionIds.has(q.section_id))
            .map((q) => ({
                key: q.question_id,
                label: labelFor(q),
                distribution: q.distribution ?? [],
                avg: q.all_avg,
            }));
        return { section, chartRows };
    });
});

// Detailed breakdown shows every score question across the whole campaign
// with the filter applied — same segmented bars, no section grouping.
const detailedRows = computed(() =>
    filteredScoreQuestions.value.map((q) => ({
        key: q.question_id,
        label: labelFor(q),
        distribution: q.distribution ?? [],
        avg: q.all_avg,
    })),
);

// Open-text: group text questions by section, then merge their themes. Falls
// back to top-level `report.themes` for campaigns whose text questions don't
// carry synthesized themes.
interface OpenTextGroup {
    questionId: string;
    title: string;
    responseCount: number;
    themes: { title: string; summary?: string | null }[];
}

const openTextGroups = computed<OpenTextGroup[]>(() => {
    const groups: OpenTextGroup[] = [];
    const textQuestions = aggregates.value?.per_question?.filter((q) => q.is_text) ?? [];
    for (const q of textQuestions) {
        if (!q.open_text_synthesis && !(props.report.themes?.length ?? 0)) {
            continue;
        }
        // Themes are report-level today; anchor them under the first text
        // question so the mockup's "clustered into N themes" line renders.
        const themes = q.open_text_synthesis
            ? [{ title: t("reflections.campaignReport.synthesis"), summary: q.open_text_synthesis }]
            : (props.report.themes ?? []).map((theme) => ({ title: theme.title, summary: theme.summary }));
        groups.push({
            questionId: q.question_id,
            title: labelFor(q),
            responseCount: q.open_text_count ?? q.all_count ?? 0,
            themes,
        });
    }
    // If no per-question themes but campaign-level themes exist, show them
    // once as a general open-text summary.
    if (groups.length === 0 && (props.report.themes?.length ?? 0) > 0) {
        groups.push({
            questionId: "themes",
            title: t("reflections.campaignReport.openTextThemes"),
            responseCount: (props.report.themes ?? []).reduce((sum, t) => sum + ((t as { count?: number }).count ?? 0), 0),
            themes: (props.report.themes ?? []).map((theme) => ({ title: theme.title, summary: theme.summary })),
        });
    }
    return groups;
});
</script>
